'use client';

import React, { useState, useEffect, useCallback } from 'react';
import { createClient } from '@/lib/supabase/client';
import Icon from '@/components/ui/AppIcon';
import Link from 'next/link';

const supabase = createClient();

interface NotificationHistoryItem {
  id: string;
  tipo: string;
  titolo: string;
  canale: string;
  destinatario: string;
  stato: string;
  created_at: string;
}

const STATO_COLORS: Record<string, string> = {
  inviata: 'text-emerald-400',
  fallita: 'text-red-400',
  in_attesa: 'text-yellow-400',
  annullata: 'text-gray-400',
};

const TIPO_ICONS: Record<string, string> = {
  email: 'EnvelopeIcon',
  sms: 'DevicePhoneMobileIcon',
  trigger: 'BoltIcon',
  sistema: 'CpuChipIcon',
};

export default function NotificationHistoryWidget() {
  const [items, setItems] = useState<NotificationHistoryItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [stats, setStats] = useState({ totale: 0, inviate: 0, fallite: 0 });

  const fetchHistory = useCallback(async () => {
    setLoading(true);
    const { data } = await supabase
      .from('notification_history')
      .select('id, tipo, titolo, canale, destinatario, stato, created_at')
      .order('created_at', { ascending: false })
      .limit(8);

    const items = (data as NotificationHistoryItem[]) || [];
    setItems(items);

    // Fetch stats (last 24h)
    const since = new Date(Date.now() - 24 * 60 * 60 * 1000).toISOString();
    const { data: allData } = await supabase
      .from('notification_history')
      .select('stato')
      .gte('created_at', since);

    const all = allData || [];
    setStats({
      totale: all.length,
      inviate: all.filter((n: any) => n.stato === 'inviata').length,
      fallite: all.filter((n: any) => n.stato === 'fallita').length,
    });
    setLoading(false);
  }, []);

  useEffect(() => { fetchHistory(); }, [fetchHistory]);

  const formatTime = (d: string) => {
    const diff = Date.now() - new Date(d).getTime();
    const mins = Math.floor(diff / 60000);
    if (mins < 1) return 'Adesso';
    if (mins < 60) return `${mins}m fa`;
    const hrs = Math.floor(mins / 60);
    if (hrs < 24) return `${hrs}h fa`;
    return new Date(d).toLocaleDateString('it-IT', { day: '2-digit', month: '2-digit' });
  };

  return (
    <div className="bg-surface border border-border rounded-2xl p-5 space-y-4">
      <div className="flex items-center justify-between">
        <h3 className="text-base font-bold text-white flex items-center gap-2">
          <Icon name="BellIcon" size={18} className="text-sky-400" />
          Storico Notifiche
        </h3>
        <Link href="/admin/notifiche/log" className="text-xs text-sky-400 hover:text-sky-300 transition-colors">
          Vedi tutto →
        </Link>
      </div>

      {/* 24h Stats */}
      <div className="grid grid-cols-3 gap-2">
        {[
          { l: 'Ultime 24h', v: stats.totale, color: 'text-white' },
          { l: 'Inviate', v: stats.inviate, color: 'text-emerald-400' },
          { l: 'Fallite', v: stats.fallite, color: 'text-red-400' },
        ].map(s => (
          <div key={s.l} className="bg-surface-elevated rounded-lg p-2 text-center">
            <p className={`text-lg font-bold ${s.color}`}>{s.v}</p>
            <p className="text-xs text-muted-color">{s.l}</p>
          </div>
        ))}
      </div>

      {loading ? (
        <div className="flex items-center justify-center py-6">
          <div className="w-6 h-6 border-2 border-sky-500 border-t-transparent rounded-full animate-spin" />
        </div>
      ) : items.length === 0 ? (
        <div className="text-center py-6">
          <Icon name="BellSlashIcon" size={28} className="text-muted-color mx-auto mb-2" />
          <p className="text-muted-color text-xs">Nessuna notifica inviata</p>
        </div>
      ) : (
        <div className="space-y-1">
          {items.map(item => (
            <div key={item.id} className="flex items-center gap-3 py-2 border-b border-border/40 last:border-0">
              <div className="flex-shrink-0">
                <Icon name={(TIPO_ICONS[item.tipo] || 'BellIcon') as any} size={14}
                  className={item.tipo === 'email' ? 'text-blue-400' : item.tipo === 'sms' ? 'text-green-400' : 'text-amber-400'} />
              </div>
              <div className="flex-1 min-w-0">
                <p className="text-white text-xs font-medium truncate">{item.titolo}</p>
                <p className="text-muted-color text-xs truncate">{item.destinatario}</p>
              </div>
              <div className="flex-shrink-0 text-right">
                <p className={`text-xs font-semibold ${STATO_COLORS[item.stato] || 'text-gray-400'}`}>
                  {item.stato === 'inviata' ? '✓' : item.stato === 'fallita' ? '✗' : '○'}
                </p>
                <p className="text-xs text-muted-color">{formatTime(item.created_at)}</p>
              </div>
            </div>
          ))}
        </div>
      )}

      <Link href="/admin/notifiche/test-canali"
        className="flex items-center justify-center gap-2 w-full bg-surface-elevated hover:bg-border text-muted-color hover:text-white py-2 rounded-lg text-xs font-semibold transition-colors border border-border">
        <Icon name="BeakerIcon" size={14} />
        Test Canali
      </Link>
    </div>
  );
}
